import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { blogAPI } from '../utils/api';
import { Card, Typography, Spin, Button, message, Tag, Space, Divider, Popconfirm } from 'antd';
import { CalendarOutlined, EyeOutlined, LikeOutlined, FolderOutlined, ArrowLeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import CommentList from '../components/CommentList';

const { Title, Text, Paragraph } = Typography;

const BlogDetail = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [blog, setBlog] = useState(null);

  // 获取当前登录用户
  const currentUser = JSON.parse(localStorage.getItem('current_user') || 'null');
  const isOwner = currentUser?.username === username;

  const queryParams = new URLSearchParams(window.location.search);
  const blogId = queryParams.get('id');

  useEffect(() => {
    if (blogId) {
      fetchBlogDetail(blogId);
    } else {
      message.error('博客ID不能为空');
      navigate(`/${username}/blog/`);
    }
  }, [blogId, username]);

  // 获取博客详情
  const fetchBlogDetail = async (blogId) => {
    try {
      setLoading(true);
      const response = await blogAPI.getBlog(blogId);
      setBlog(response.data);
    } catch (error) {
      navigate(`/${username}/error`, {
        state: {
          errorInfo: {
            title: '博客不存在或无权限',
            description: '博客不存在或您没有权限访问该博客'
          }
        },
        replace: true
      });
    } finally {
      setLoading(false);
    }
  };

  // 返回列表视图
  const handleBackToList = () => {
    navigate(`/${username}/blog/`);
  };

  // 删除博客
  const handleDeleteBlog = async () => {
    try {
      await blogAPI.deleteBlog(blogId);
      message.success('博客删除成功');
      navigate(`/${username}/blog/`);
    } catch (error) {
      message.error('博客删除失败');
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '未知日期';
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div style={{ width: '100%', maxWidth: '1320px', margin: '0 auto', background: '#fff', borderRadius: 8, padding: 24 }}>
        <Button type="link" onClick={handleBackToList} icon={<ArrowLeftOutlined />} style={{ paddingLeft: 0 }}>
          返回博客列表
        </Button>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Title level={3}>博客不存在</Title>
          <Text type="secondary">您请求的博客文章不存在或已被删除</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', maxWidth: '1320px', margin: '0 auto', background: '#fff', borderRadius: 8, padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Button
          type="link"
          onClick={handleBackToList}
          icon={<ArrowLeftOutlined />}
          style={{ paddingLeft: 0 }}
        >
          返回博客列表
        </Button>
        {isOwner && (
          <Space>
            <Button
              type="default"
              icon={<EditOutlined />}
              onClick={() => navigate(`/${username}/blog/article/edit/?id=${blogId}`)}
            >
              编辑
            </Button>
            <Popconfirm
              title="确认删除"
              description="确定要删除这篇博客吗？"
              onConfirm={handleDeleteBlog}
              okText="确定"
              cancelText="取消"
            >
              <Button type="primary" danger icon={<DeleteOutlined />}>
                删除
              </Button>
            </Popconfirm>
          </Space>
        )}
      </div>

      <Card>
        <Title level={1}>{blog.title}</Title>

        <Space size="middle" style={{ marginBottom: 16 }}>
          <Text type="secondary">
            <CalendarOutlined /> 发布时间：{formatDate(blog.publishTime || blog.createTime)}
          </Text>
          <Text type="secondary">
            <EyeOutlined /> 阅读：{blog.viewCount || 0}
          </Text>
          <Text type="secondary">
            <LikeOutlined /> 点赞：{blog.likeCount || 0}
          </Text>
          {blog.category && (
            <Tag color="blue" icon={<FolderOutlined />}>
              {blog.category}
            </Tag>
          )}
        </Space>

        <Divider />

        {/* 处理HTML内容或纯文本内容 */}
        {blog.content ? (
          blog.content.startsWith('<') ? (
            <div
              dangerouslySetInnerHTML={{ __html: blog.content }}
              style={{
                lineHeight: 1.8,
                fontSize: '16px'
              }}
            />
          ) : (
            <Paragraph style={{
              whiteSpace: 'pre-wrap',
              lineHeight: 1.8,
              fontSize: '16px'
            }}>
              {blog.content}
            </Paragraph>
          )
        ) : (
          <Text type="secondary">暂无内容</Text>
        )}
      </Card>

      {/* 评论区 */}
      <CommentList blogId={Number(blogId)} username={username} />
    </div>
  );
};

export default BlogDetail;